import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import PhotoUpload from "../../components/PhotoUpload";
import { createLookupApi } from "../../api/lookupApi";
import { getStaff, createStaff, updateStaff, uploadStaffPhoto } from "../../api/staffApi";
import { listTeacherSections } from "../../api/teacherSectionApi";
import "../Students/Students.css";

const designationApi = createLookupApi("designations");

const emptyForm = {
  customStaffId: "",
  staffName: "",
  nickName: "",
  gender: "Male",
  religion: "",
  staffAddress: "",
  fatherName: "",
  motherName: "",
  mobileNumber: "",
  email: "",
  bloodGroup: "",
  birthDate: "",
  employmentDate: "",
  resignDate: "",
  idCardIssue: "",
  idCardExpire: "",
  designationId: "",
  teacherSectionId: "",
  qualification: "",
  staffNid: "",
  subjectExpertise: "",
  indexNo: "",
  appointingAuthority: "",
  staffStatus: 1,
};

export default function StaffFormPage() {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyForm);
  const [imageName, setImageName] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [teacherSections, setTeacherSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const lookups = Promise.all([designationApi.list(), listTeacherSections()]);
    const staffLoad = isEdit ? getStaff(id) : Promise.resolve(null);

    Promise.all([lookups, staffLoad])
      .then(([[designationList, teacherSectionList], staff]) => {
        setDesignations(designationList);
        setTeacherSections(teacherSectionList);
        if (staff) {
          setForm({
            ...emptyForm,
            customStaffId: staff.customStaffId || "",
            staffName: staff.staffName || "",
            nickName: staff.nickName || "",
            gender: staff.gender || "Male",
            religion: staff.religion || "",
            staffAddress: staff.staffAddress || "",
            fatherName: staff.fatherName || "",
            motherName: staff.motherName || "",
            mobileNumber: staff.mobileNumber || "",
            email: staff.email || "",
            bloodGroup: staff.bloodGroup || "",
            birthDate: staff.birthDate || "",
            employmentDate: staff.employmentDate || "",
            resignDate: staff.resignDate || "",
            idCardIssue: staff.idCardIssue || "",
            idCardExpire: staff.idCardExpire || "",
            designationId: staff.designationId || "",
            teacherSectionId: staff.teacherSectionId || "",
            qualification: staff.qualification || "",
            staffNid: staff.staffNid || "",
            subjectExpertise: staff.subjectExpertise || "",
            indexNo: staff.indexNo || "",
            appointingAuthority: staff.appointingAuthority || "",
            staffStatus: staff.staffStatus,
          });
          setImageName(staff.imageName);
        }
      })
      .catch(() => setError("Failed to load form data."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...form,
        designationId: form.designationId ? Number(form.designationId) : null,
        teacherSectionId: form.teacherSectionId ? Number(form.teacherSectionId) : null,
      };
      if (isEdit) {
        await updateStaff(id, { ...payload, staffStatus: Number(form.staffStatus) });
        setSuccess("Staff updated.");
      } else {
        delete payload.staffStatus;
        delete payload.resignDate;
        delete payload.idCardIssue;
        delete payload.idCardExpire;
        const created = await createStaff(payload);
        setSuccess("Staff registered successfully.");
        history.push(`/staff/${created.staffId}/edit`);
      }
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    const updated = await uploadStaffPhoto(id, file);
    setImageName(updated.imageName);
  };

  if (loading) {
    return (
      <MainLayout>
        <p>Loading...</p>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{isEdit ? "Edit Staff" : "Register Staff"}</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="student-form-page">
        {isEdit && (
          <div className="form-section">
            <h2>Photo</h2>
            <PhotoUpload folder="staff" fileName={imageName} onUpload={handlePhotoUpload} />
          </div>
        )}

        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            {isEdit && (
              <label>
                Custom Staff ID *
                <input value={form.customStaffId} onChange={set("customStaffId")} required />
              </label>
            )}
            <label>
              Staff Name *
              <input value={form.staffName} onChange={set("staffName")} required />
            </label>
            <label>
              Nick Name
              <input value={form.nickName} onChange={set("nickName")} />
            </label>
            <label>
              Gender
              <select value={form.gender} onChange={set("gender")}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label>
              Religion
              <input value={form.religion} onChange={set("religion")} />
            </label>
            <label>
              Mobile Number *
              <input value={form.mobileNumber} onChange={set("mobileNumber")} required />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={set("email")} />
            </label>
            <label>
              Blood Group
              <input value={form.bloodGroup} onChange={set("bloodGroup")} />
            </label>
            <label>
              Address
              <input value={form.staffAddress} onChange={set("staffAddress")} />
            </label>
            <label>
              Father's Name
              <input value={form.fatherName} onChange={set("fatherName")} />
            </label>
            <label>
              Mother's Name
              <input value={form.motherName} onChange={set("motherName")} />
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Employment</h2>
          <div className="form-grid">
            <label>
              Designation
              <select value={form.designationId} onChange={set("designationId")}>
                <option value="">None</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Teacher Section
              <select value={form.teacherSectionId} onChange={set("teacherSectionId")}>
                <option value="">None</option>
                {teacherSections.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Birth Date
              <input type="date" value={form.birthDate} onChange={set("birthDate")} />
            </label>
            <label>
              Employment Date
              <input type="date" value={form.employmentDate} onChange={set("employmentDate")} />
            </label>
            {isEdit && (
              <label>
                Resign Date
                <input type="date" value={form.resignDate} onChange={set("resignDate")} />
              </label>
            )}
            {isEdit && (
              <label>
                ID Card Issue Date
                <input type="date" value={form.idCardIssue} onChange={set("idCardIssue")} />
              </label>
            )}
            {isEdit && (
              <label>
                ID Card Expire Date
                <input type="date" value={form.idCardExpire} onChange={set("idCardExpire")} />
              </label>
            )}
            <label>
              Qualification
              <input value={form.qualification} onChange={set("qualification")} />
            </label>
            <label>
              NID
              <input value={form.staffNid} onChange={set("staffNid")} />
            </label>
            <label>
              Subject Expertise
              <input value={form.subjectExpertise} onChange={set("subjectExpertise")} />
            </label>
            <label>
              Index No
              <input value={form.indexNo} onChange={set("indexNo")} />
            </label>
            <label>
              Appointing Authority
              <input value={form.appointingAuthority} onChange={set("appointingAuthority")} />
            </label>
            {isEdit && (
              <label>
                Status
                <select value={form.staffStatus} onChange={set("staffStatus")}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </label>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Register Staff"}
          </button>
        </div>
      </form>
    </MainLayout>
  );
}
